import { AppModal, Button, Input, Label, Textarea } from "@/components/ui";
import {
  useCreateReusablePhase,
  useUpdateReusablePhase,
} from "@/lib/data/react-query/matters/matter-phase";
import type { ReusableMatterPhase } from "@/lib/types";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

export function ReusablePhaseEditorModal({
  open,
  onOpenChange,
  phase,
  onCreated,
  stacked = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  phase?: ReusableMatterPhase;
  onCreated?: (phase: ReusableMatterPhase) => void;
  stacked?: boolean;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const createPhase = useCreateReusablePhase();
  const updatePhase = useUpdateReusablePhase();
  const isPending = createPhase.isPending || updatePhase.isPending;

  useEffect(() => {
    if (!open) return;
    setTitle(phase?.title ?? "");
    setDescription(phase?.description ?? "");
  }, [open, phase]);

  const isDirty =
    title !== (phase?.title ?? "") ||
    description !== (phase?.description ?? "");

  const submit = async () => {
    const nextTitle = title.trim();
    if (!nextTitle) {
      toast.error("Phase title is required.");
      return;
    }
    const payload = {
      title: nextTitle,
      description: description.trim() || null,
    };
    if (phase) {
      await updatePhase.mutateAsync({
        reusablePhaseId: phase.id,
        dateModified: phase.dateModified,
        ...payload,
      });
    } else {
      const response = await createPhase.mutateAsync(payload);
      onCreated?.(response.data);
    }
    onOpenChange(false);
  };

  return (
    <AppModal
      open={open}
      onOpenChange={onOpenChange}
      title={phase ? "Edit reusable phase" : "Create reusable phase"}
      description="Reusable phases provide the shared name and description used when building phase projects."
      size="lg"
      loading={isPending}
      isDirty={isDirty}
      overlayClassName={stacked ? "z-[108]" : undefined}
      className={stacked ? "z-[109]" : undefined}
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button onClick={() => void submit()}>
            {phase ? "Save phase" : "Create phase"}
          </Button>
        </div>
      }
    >
      <fieldset disabled={isPending} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="reusable-phase-title">Title</Label>
          <Input
            id="reusable-phase-title"
            value={title}
            maxLength={200}
            onChange={(event) => {
              setTitle(event.target.value);
            }}
            placeholder="Investigation and clearing"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="reusable-phase-description">Description</Label>
          <Textarea
            id="reusable-phase-description"
            value={description}
            maxLength={5000}
            rows={5}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
            placeholder="Describe the purpose of this phase"
          />
        </div>
      </fieldset>
    </AppModal>
  );
}
